// =====================================================
// Apple Design Enhancement - Progressive Animation Hook
// Implements staggered list animations with eased durations
// where later items animate longer for a fluid, polished feel
// =====================================================

import { useState, useEffect, useMemo } from 'react';

// =====================================================
// Types
// =====================================================

/**
 * Configuration for progressive animation timing
 */
export interface AnimationConfig {
  /**
   * Base delay between items in milliseconds.
   * Default: 80ms
   */
  baseDelay: number;
  
  /**
   * Base animation duration in milliseconds.
   * Default: 150ms
   */
  baseDuration: number;
  
  /**
   * Additional duration spread for later items in milliseconds.
   * Later items will have duration up to baseDuration + durationSpread.
   * Default: 300ms
   */
  durationSpread: number;
  
  /**
   * Easing function for calculating duration spread.
   * Takes a normalized value (0-1) and returns an eased value (0-1).
   * Default: easeOutCubic
   */
  easing: (t: number) => number;
}

/**
 * Animation style properties to apply to animated elements
 */
export interface AnimationStyle {
  /**
   * CSS animation-delay value (e.g., "80ms")
   */
  animationDelay: string;
  
  /**
   * CSS animation-duration value (e.g., "150ms")
   */
  animationDuration: string;
  
  /**
   * Initial opacity for animation (0 for enter, 1 for exit)
   */
  opacity: number;
  
  /**
   * CSS transform value for animation
   */
  transform: string;
}

/**
 * Return type for useProgressiveAnimation hook
 */
export interface UseProgressiveAnimationReturn {
  /**
   * Animation styles to apply to the element
   */
  style: AnimationStyle;
  
  /**
   * Whether animations are enabled (false if user prefers reduced motion)
   */
  animationsEnabled: boolean;
  
  /**
   * Calculated delay in milliseconds
   */
  delay: number;
  
  /**
   * Calculated duration in milliseconds
   */
  duration: number;
}

/**
 * Direction of animation
 */
export type AnimationDirection = 'enter' | 'exit';

// =====================================================
// Constants
// =====================================================

/** Default base delay between items (80ms per requirement 4.3) */
export const BASE_DELAY = 80;

/** Default base animation duration (150ms per requirement 4.4) */
export const BASE_DURATION = 150;

/** Default duration spread for later items (300ms per requirement 4.4) */
export const DURATION_SPREAD = 300;

/** Exit animation duration multiplier (shorter durations for exit) */
export const EXIT_DURATION_MULTIPLIER = 0.6;

// =====================================================
// Easing Functions
// =====================================================

/**
 * EaseOutCubic easing function.
 * Creates a smooth deceleration curve where animation starts fast and slows down.
 * 
 * @param t - Normalized time value (0 to 1)
 * @returns Eased value (0 to 1)
 */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Linear easing function (no easing).
 * 
 * @param t - Normalized time value (0 to 1)
 * @returns Same value (0 to 1)
 */
export function linear(t: number): number {
  return t;
}

/**
 * EaseInCubic easing function.
 * Creates a smooth acceleration curve.
 * 
 * @param t - Normalized time value (0 to 1)
 * @returns Eased value (0 to 1)
 */
export function easeInCubic(t: number): number {
  return Math.pow(t, 3);
}

// =====================================================
// Default Configuration
// =====================================================

const DEFAULT_CONFIG: AnimationConfig = {
  baseDelay: BASE_DELAY,
  baseDuration: BASE_DURATION,
  durationSpread: DURATION_SPREAD,
  easing: easeOutCubic,
};

// =====================================================
// Helper Functions
// =====================================================

/**
 * Hook to detect user's reduced motion preference.
 * 
 * @returns Whether the user prefers reduced motion
 * 
 * Requirements:
 * - 4.5: WHEN the user prefers reduced motion, THE Progressive_Animation SHALL skip animations and render items immediately
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Use addEventListener for modern browsers
    mediaQuery.addEventListener('change', handler);
    
    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Calculate the eased animation duration for an item based on its position.
 * Later items in the list will have longer animation durations, creating
 * a fluid, cascading effect.
 * 
 * @param index - The item's index in the list (0-based)
 * @param totalItems - Total number of items in the list
 * @param easing - Easing function to apply (default: easeOutCubic)
 * @returns Duration in milliseconds
 * 
 * Requirements:
 * - 4.2: THE Progressive_Animation SHALL calculate animation duration using an easing function where later items animate longer
 * - 4.4: THE Progressive_Animation SHALL use a base duration of 150ms with a spread of up to 300ms for later items
 */
export function getEasedDuration(
  index: number,
  totalItems: number,
  easing: (t: number) => number = easeOutCubic
): number {
  // Handle edge cases
  if (totalItems <= 0) {
    return BASE_DURATION;
  }
  
  if (totalItems === 1) {
    return BASE_DURATION;
  }
  
  // Clamp index to valid range
  const clampedIndex = Math.max(0, Math.min(index, totalItems - 1));
  
  // Calculate normalized position (0 to 1)
  const normalizedPosition = clampedIndex / (totalItems - 1);
  
  // Apply easing function to get eased position
  const easedPosition = easing(normalizedPosition);
  
  // Calculate final duration: base + (spread * easedPosition)
  const duration = BASE_DURATION + (DURATION_SPREAD * easedPosition);
  
  return Math.round(duration);
}

/**
 * Calculate the animation delay for an item based on its position.
 * 
 * @param index - The item's index in the list (0-based)
 * @param baseDelay - Base delay between items in milliseconds
 * @param direction - Animation direction ('enter' or 'exit')
 * @param totalItems - Total number of items (used for exit reverse order)
 * @returns Delay in milliseconds
 * 
 * Requirements:
 * - 4.1: WHEN a list renders, THE Progressive_Animation SHALL stagger item entrance with increasing delays
 * - 4.3: THE Progressive_Animation SHALL use a base delay of 80ms between items
 * - 4.6: WHEN items exit the list, THE Progressive_Animation SHALL animate them out in reverse order with shorter durations
 */
export function getAnimationDelay(
  index: number,
  baseDelay: number = BASE_DELAY,
  direction: AnimationDirection = 'enter',
  totalItems: number = 0
): number {
  // Handle edge cases
  if (index < 0) {
    return 0;
  }
  
  if (direction === 'exit' && totalItems > 0) {
    // For exit animations, reverse the order (last item exits first)
    const reverseIndex = totalItems - 1 - index;
    return Math.max(0, reverseIndex) * baseDelay;
  }
  
  // For enter animations, delay increases with index
  return index * baseDelay;
}

/**
 * Calculate the animation duration for exit animations.
 * Exit animations are shorter than enter animations.
 * 
 * @param enterDuration - The enter animation duration
 * @returns Exit animation duration in milliseconds
 * 
 * Requirements:
 * - 4.6: WHEN items exit the list, THE Progressive_Animation SHALL animate them out in reverse order with shorter durations
 */
export function getExitDuration(enterDuration: number): number {
  return Math.round(enterDuration * EXIT_DURATION_MULTIPLIER);
}

// =====================================================
// Main Hook
// =====================================================

/**
 * Hook for progressive list animations with staggered timing.
 * 
 * This hook calculates animation delay and duration for list items,
 * creating a fluid, cascading animation effect where:
 * - Items animate in sequence with increasing delays
 * - Later items have longer animation durations
 * - Exit animations play in reverse order with shorter durations
 * - Animations are disabled when user prefers reduced motion
 * 
 * @param index - The item's index in the list (0-based)
 * @param totalItems - Total number of items in the list
 * @param config - Optional configuration overrides
 * @param direction - Animation direction ('enter' or 'exit')
 * @returns Animation styles and metadata
 * 
 * @example
 * ```tsx
 * function FileList({ files }) {
 *   return (
 *     <ul>
 *       {files.map((file, index) => (
 *         <FileItem
 *           key={file.id}
 *           file={file}
 *           animationProps={useProgressiveAnimation(index, files.length)}
 *         />
 *       ))}
 *     </ul>
 *   );
 * }
 * 
 * function FileItem({ file, animationProps }) {
 *   const { style, animationsEnabled } = animationProps;
 *   
 *   return (
 *     <li
 *       style={animationsEnabled ? {
 *         animationDelay: style.animationDelay,
 *         animationDuration: style.animationDuration,
 *       } : undefined}
 *       className={animationsEnabled ? 'animate-fade-in-up' : ''}
 *     >
 *       {file.name}
 *     </li>
 *   );
 * }
 * ```
 * 
 * Requirements:
 * - 4.1: WHEN a list renders, THE Progressive_Animation SHALL stagger item entrance with increasing delays
 * - 4.2: THE Progressive_Animation SHALL calculate animation duration using an easing function where later items animate longer
 * - 4.3: THE Progressive_Animation SHALL use a base delay of 80ms between items
 * - 4.4: THE Progressive_Animation SHALL use a base duration of 150ms with a spread of up to 300ms for later items
 * - 4.5: WHEN the user prefers reduced motion, THE Progressive_Animation SHALL skip animations and render items immediately
 * - 4.6: WHEN items exit the list, THE Progressive_Animation SHALL animate them out in reverse order with shorter durations
 */
export function useProgressiveAnimation(
  index: number,
  totalItems: number,
  config?: Partial<AnimationConfig>,
  direction: AnimationDirection = 'enter'
): UseProgressiveAnimationReturn {
  // Merge config with defaults
  const mergedConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config,
  }), [config]);
  
  // Check for reduced motion preference
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Calculate animation timing
  const { delay, duration } = useMemo(() => {
    // If reduced motion is preferred, return zero values
    if (prefersReducedMotion) {
      return { delay: 0, duration: 0 };
    }
    
    const { baseDelay, baseDuration, durationSpread, easing } = mergedConfig;
    
    // Calculate delay based on direction
    const calculatedDelay = getAnimationDelay(index, baseDelay, direction, totalItems);
    
    // Calculate base duration using easing
    let calculatedDuration = getEasedDuration(index, totalItems, easing);
    
    // Apply custom base duration and spread if provided
    if (config?.baseDuration !== undefined || config?.durationSpread !== undefined) {
      const normalizedPosition = totalItems > 1 
        ? Math.max(0, Math.min(index, totalItems - 1)) / (totalItems - 1)
        : 0;
      const easedPosition = easing(normalizedPosition);
      calculatedDuration = baseDuration + (durationSpread * easedPosition);
    }
    
    // Apply exit duration multiplier for exit animations
    if (direction === 'exit') {
      calculatedDuration = getExitDuration(calculatedDuration);
    }
    
    return {
      delay: calculatedDelay,
      duration: Math.round(calculatedDuration),
    };
  }, [index, totalItems, mergedConfig, direction, prefersReducedMotion, config]);
  
  // Build animation style object
  const style = useMemo((): AnimationStyle => {
    if (prefersReducedMotion) {
      // No animation - render immediately in final state
      return {
        animationDelay: '0ms',
        animationDuration: '0ms',
        opacity: 1,
        transform: 'none',
      };
    }
    
    const isEnter = direction === 'enter';
    
    return {
      animationDelay: `${delay}ms`,
      animationDuration: `${duration}ms`,
      opacity: isEnter ? 0 : 1,
      transform: isEnter ? 'translateY(20px)' : 'translateY(0)',
    };
  }, [delay, duration, direction, prefersReducedMotion]);
  
  return {
    style,
    animationsEnabled: !prefersReducedMotion,
    delay,
    duration,
  };
}

export default useProgressiveAnimation;
